import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LOCALS } from '@constants/config';
import { API_DOMAIN } from '@constants/api';
import { parseSeatGame, visibleRegions } from '@constants/seatGroups';

/**
 * 圖片直接抓遊戲主機：https://{region}.ace23.cc/images/g/{c|e}{gameId}.jpg
 */
const getCasinoImageUrl = (gameId, language) => {
    const baseDomain = API_DOMAIN.replace('/api', '');
    const lang = language === LOCALS.cn ? 'c' : 'e';
    return `${baseDomain}/images/g/${lang}${gameId}.jpg`;
};

/**
 * 入座制遊戲卡片。game 為 getUserInfo 清單的原始陣列：
 * 12 欄（四區 入座/座位 pair）或 6 欄舊格式（單組 入座/總數）。
 */
export default function CasinoGameCard({ game, onClick }) {
    const { i18n } = useTranslation();
    const parsed = parseSeatGame(game);

    // 未知欄數（格式漂移，如過渡期 9 欄）：前 4 欄位置固定，fallback 只顯示
    // 名稱與圖、不顯示座位數——避免整頁卡片無聲消失
    const fallback = !parsed && Array.isArray(game) && game.length >= 4;
    if (!parsed && !fallback) return null;
    if (fallback) {
        console.warn('CasinoGameCard: unexpected game format, length =', game.length, game[1]);
    }

    const isChinese = i18n.language === LOCALS.cn;
    const displayName = isChinese ? (parsed?.nameCN ?? game[3]) : (parsed?.nameEN ?? game[2]);
    const imageUrl = getCasinoImageUrl(parsed?.id ?? game[1], i18n.language);
    const regions = parsed && !parsed.legacy ? visibleRegions(parsed) : null;

    return (
        <CardWrapper onClick={() => onClick(game)}>
            <ImageWrapper>
                <img
                    src={imageUrl}
                    alt={displayName}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/shared/placeholder.png';
                    }}
                />
            </ImageWrapper>
            <CardInfo>
                <GameName>{displayName}</GameName>
                {fallback ? null : parsed.legacy ? (
                    <SeatCount>{parsed.used}/{parsed.total}</SeatCount>
                ) : (
                    <SeatCount>
                        {regions.map((r) => (
                            <RegionPair key={r.key} $color={r.color}>
                                {r.used}/{r.total}
                            </RegionPair>
                        ))}
                    </SeatCount>
                )}
            </CardInfo>
        </CardWrapper>
    );
}

const CardWrapper = styled.div`
    width: 100%;
    cursor: pointer;
`;

const ImageWrapper = styled.div`
    img {
        display: block;
        padding: 0.1rem;
        width: 90%;
        margin: 0 auto;
        border-radius: 6px;
    }
`;

const CardInfo = styled.div`
    width: 100%;
    margin: 0 auto;
    text-align: center;
`;

const GameName = styled.p`
    width: 100%;
    margin: 0 auto;
    font-size: 14px;
    text-align: center;
    word-break: break-word;
`;

const SeatCount = styled.p`
    margin: 0;
    font-size: 12px;
    color: #666;
`;

const RegionPair = styled.span`
    color: ${({ $color }) => $color};
    font-weight: 600;
    & + & {
        margin-left: 6px;
    }
`;
